import api from '../../utils/api'
import { useState, useEffect, useMemo } from 'react'
import Select from 'react-select'

import Input from '../../shared/components/form/input'
import Button from '../../shared/components/Button/Button'
import selectStyles from '../../shared/css/SelectStyle'
import buildCategoryOptions from '../../shared/functions/buildCategoryOptions'
import { FINANCE_STATUS_LABEL } from '../../shared/constants/financePaymentMethods'
import TimestampToString from '../../shared/functions/TimestampToString'
import useMyPermission from '../../hooks/useMyPermission'

import './Finance.css'

const TYPE_OPTIONS = [
    { value: 'pagar', label: 'Contas a Pagar' },
    { value: 'receber', label: 'Contas a Receber' },
]
const STATUS_OPTIONS = Object.entries(FINANCE_STATUS_LABEL).map(([value, label]) => ({ value, label }))

// Relatório filtrável — mesma fonte de dados de Contas a Pagar/Receber
// (GET /finance/entry/getall), mas achatado em linhas de PARCELA (não de lançamento), pra dar
// o total certo por categoria/período mesmo quando um lançamento tem parcelas em datas
// diferentes. Exporta via impressão do navegador, mesmo padrão já usado no Relatório de Pedido.
function Report() {
    const [token] = useState(localStorage.getItem('token') || '')
    const { timeStampToDDMMAAAA } = TimestampToString()

    const [type, setType] = useState(TYPE_OPTIONS[0])
    const [entries, setEntries] = useState([])
    const [categories, setCategories] = useState([])
    const [load, setLoad] = useState(false)

    const [statusFilter, setStatusFilter] = useState(null)
    const [categoryFilter, setCategoryFilter] = useState(null)
    const [startDate, setStartDate] = useState('')
    const [endDate, setEndDate] = useState('')
    const [counterpartSearch, setCounterpartSearch] = useState('')

    const [myPermission, setMyPermission] = useState({ group: '' })
    const { checkPermission } = useMyPermission()
    const myPermissionView = checkPermission(myPermission, 'finance_view')

    const categoryOptions = useMemo(() => buildCategoryOptions(categories), [categories])

    const getAll = () => {
        Promise.all([
            api.get(`/finance/entry/getall?type=${type.value}`, { headers: { Authorization: `Bearer ${JSON.parse(token)}` } }),
            api.get('/finance/category/getall', { headers: { Authorization: `Bearer ${JSON.parse(token)}` } }),
        ]).then(([entryRes, categoryRes]) => {
            setEntries(entryRes.data.entries || [])
            setMyPermission(entryRes.data.permission)
            setCategories(categoryRes.data.categories || [])
            setLoad(true)
        })
    }

    useEffect(() => {
        getAll()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, type])

    // Uma linha por parcela — categoria/valor da linha é o rateio do lançamento aplicado
    // proporcionalmente (se tem 2 parcelas e 2 categorias 50/50, cada parcela carrega metade de
    // cada categoria).
    const rows = useMemo(() => {
        const list = []
        entries.forEach((entry) => {
            if (categoryFilter && !entry.allocations.some((a) => (a.category?._id || a.category) === categoryFilter.value)) return
            if (counterpartSearch && !(entry.counterpart?.name || '').toLowerCase().includes(counterpartSearch.toLowerCase())) return

            entry.parcels.forEach((p) => {
                if (statusFilter && p.status !== statusFilter.value) return
                if (startDate && new Date(p.dueDate) < new Date(startDate)) return
                if (endDate && new Date(p.dueDate) > new Date(endDate)) return

                const ratio = entry.totalValue > 0 ? p.value / entry.totalValue : 0
                entry.allocations.forEach((a) => {
                    if (!a.category) return
                    if (categoryFilter && a.category._id !== categoryFilter.value && a.category !== categoryFilter.value) return
                    list.push({
                        entryId: entry._id,
                        description: entry.description,
                        counterpart: entry.counterpart?.name || '—',
                        category: a.category.name,
                        dueDate: p.dueDate,
                        status: p.status,
                        value: a.value * ratio,
                    })
                })
            })
        })
        return list
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entries, statusFilter, categoryFilter, startDate, endDate, counterpartSearch])

    const totalsByCategory = useMemo(() => {
        const totals = {}
        rows.forEach((r) => { totals[r.category] = (totals[r.category] || 0) + r.value })
        return Object.entries(totals).sort((a, b) => b[1] - a[1])
    }, [rows])

    const grandTotal = rows.reduce((acc, r) => acc + r.value, 0)

    if (!load) {
        return <div className="pg-loading">Carregando relatório...</div>
    }

    return myPermissionView ? (
        <section className="pg-page">

            <div className="pg-sticky-top">
                <div className="pg-header">
                    <div className="pg-header-title">
                        <p className="pg-title">Relatório Financeiro</p>
                        <span className="pg-title-accent" />
                    </div>
                    <div className="pg-header-row">
                        <div className="pg-header-right">
                            <Button variant="print" onClick={() => window.print()}>Imprimir</Button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="fin-report-filters">
                <div style={{ minWidth: 180 }}>
                    <Select options={TYPE_OPTIONS} styles={selectStyles} value={type} onChange={setType} menuPortalTarget={document.body} menuPosition="fixed" />
                </div>
                <div style={{ minWidth: 160 }}>
                    <Select options={STATUS_OPTIONS} styles={selectStyles} value={statusFilter} onChange={setStatusFilter} isClearable placeholder="Status..." menuPortalTarget={document.body} menuPosition="fixed" />
                </div>
                <div style={{ minWidth: 220 }}>
                    <Select options={categoryOptions} styles={selectStyles} value={categoryFilter} onChange={setCategoryFilter} isClearable placeholder="Categoria..." menuPortalTarget={document.body} menuPosition="fixed" />
                </div>
                <div className="form-group">
                    <Input type="date" name="startDate" placeholder="De" handleOnChange={(e) => setStartDate(e.target.value)} value={startDate} />
                </div>
                <div className="form-group">
                    <Input type="date" name="endDate" placeholder="Até" handleOnChange={(e) => setEndDate(e.target.value)} value={endDate} />
                </div>
                <div className="form-group">
                    <Input type="text" name="counterpartSearch" placeholder="Fornecedor/Cliente..." handleOnChange={(e) => setCounterpartSearch(e.target.value)} value={counterpartSearch} />
                </div>
            </div>

            {rows.length > 0 ? (
                <table className="fin-cashflow-table">
                    <thead>
                        <tr>
                            <th style={{ textAlign: 'left' }}>Descrição</th>
                            <th style={{ textAlign: 'left' }}>Contraparte</th>
                            <th style={{ textAlign: 'left' }}>Categoria</th>
                            <th style={{ textAlign: 'left' }}>Vencimento</th>
                            <th style={{ textAlign: 'left' }}>Status</th>
                            <th>Valor</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r, i) => (
                            <tr key={i}>
                                <td style={{ textAlign: 'left' }}>{r.description}</td>
                                <td style={{ textAlign: 'left' }}>{r.counterpart}</td>
                                <td style={{ textAlign: 'left' }}>{r.category}</td>
                                <td style={{ textAlign: 'left' }}>{timeStampToDDMMAAAA(new Date(r.dueDate).toISOString())}</td>
                                <td style={{ textAlign: 'left' }}><span className={`fin-status fin-status-${r.status}`}>{FINANCE_STATUS_LABEL[r.status]}</span></td>
                                <td>R$ {r.value.toFixed(2)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            ) : (
                <div className="pg-empty">
                    <p className="pg-empty-title">Nenhum lançamento com esses filtros</p>
                </div>
            )}

            {totalsByCategory.length > 0 && (
                <div className="fin-report-totals">
                    {totalsByCategory.map(([category, value]) => (
                        <div key={category} className="fin-report-total-row">
                            <span>{category}</span>
                            <span>R$ {value.toFixed(2)}</span>
                        </div>
                    ))}
                    <div className="fin-report-total-row fin-report-total-row-grand">
                        <span>Total</span>
                        <span>R$ {grandTotal.toFixed(2)}</span>
                    </div>
                </div>
            )}

        </section>
    ) : (
        <div className="pg-empty">
            <p className="pg-empty-title">Sem permissão para visualizar o Financeiro</p>
        </div>
    )
}

export default Report
